<script setup lang="ts">
    import { CardTitle } from '@/components/ui/card';
    import { computed, watch } from 'vue';
    import { createPayrollColumns } from '@/tables/columns/payroll';
    import { Head, router } from '@inertiajs/vue3';
    import { useI18n } from 'vue-i18n';
    import { usePage } from '@inertiajs/vue3';
    import { usePermissions } from '@/composables/usePermissions';
    import { useQuery } from '@tanstack/vue-query';
    import AddEmployeeToPayroll from '@/components/salary/AddEmployeeToPayroll.vue';
    import AppLayout from '@/layouts/AppLayout.vue';
    import axios from 'axios';
    import DataTable from '@/components/datatable/DataTable.vue';
    import DataTableToolbarActions from '@/components/datatable/DataTableToolbarActions.vue';
    import PageCard from '@/components/PageCard.vue';
    import PayrollApprovalAlertDialog from '@/components/salary/PayrollApprovalAlertDialog.vue';
    import PayrollFixedAddOnsAlertDialog from '@/components/salary/PayrollFixedAddOnsAlertDialog.vue';
    import PayrollGageAlertDialog from '@/components/salary/PayrollGageAlertDialog.vue';
    import PayrollLaunchArrangementMonthlyAlertDialog from '@/components/salary/PayrollLaunchArrangementMonthlyAlertDialog.vue';
    import PayrollSummary from '@/components/salary/PayrollSummary.vue';
    import PayrollUnlockAlertDialog from '@/components/salary/PayrollUnlockAlertDialog.vue';
    import Permission from '@/enums/Permission';
    import QueryKey from '@/enums/QueryKey';
    import RemoveEmployeeFromPayroll from '@/components/salary/RemoveEmployeeFromPayroll.vue';
    import type { SharedData, BreadcrumbItem, Payroll, PayrollDataField } from '@/types';

    const page = usePage<SharedData>();
    const selectedCompanyId = computed(() => page.props.auth.user.selected_company_id);
    const isClient = computed(() => !!page.props.auth.user.is_client);

    const { t } = useI18n();

    const { hasPermission } = usePermissions();

    const props = defineProps<{
        payroll: Payroll;
        editableFields: PayrollDataField[];
        commentsExists: boolean;
        isApproved: boolean;
        clientCanUnlock: boolean;
        timeSinceApproval: number;
    }>();

    const breadcrumbs: BreadcrumbItem[] = [
        {
            title: 'dashboards.payroll',
        },
        {
            title: 'payrolls.payroll_header',
            href: route('v2.salary.payrolls.index'),
        },
        {
            title: props.payroll.company.name,
            translated: true,
        },
    ];

    watch(selectedCompanyId, (newCompanyId, oldCompanyId) => {
        console.log(newCompanyId, oldCompanyId);
        if (newCompanyId !== oldCompanyId && oldCompanyId !== undefined) {
            router.visit(route('v2.salary.payrolls.index'));
        }
    });

    const { isPending, data } = useQuery({
        queryKey: [QueryKey.PAYROLL, selectedCompanyId, props.payroll.id],
        queryFn: getPayroll,
        placeholderData: [],
    });

    async function getPayroll() {
        const { data } = await axios.get(
            route('v2.salary.payroll.table', { payrollId: props.payroll.id }),
        );
        return data.data;
    }

    const columns = computed(() =>
        createPayrollColumns({
            editableFields: props.editableFields,
            payrollId: props.payroll.id,
            isApproved: props.isApproved,
        }),
    );

    const defaultColumnVisibility = computed(() => {
        return {
            id: false,
            comment: !props.isApproved || (props.isApproved && props.commentsExists),
        };
    });
</script>

<template>
    <Head :title="t('payrolls.edit.header', { company: props.payroll.company.name })" />

    <AppLayout :breadcrumbs="breadcrumbs">
        <div class="flex flex-col-reverse lg:min-h-0 lg:flex-1 lg:flex-row">
            <PageCard class="mt-0 flex min-h-96 min-w-0 flex-1 flex-col lg:mt-1.5 lg:mr-0">
                <DataTable
                    :table-key="QueryKey.PAYROLL"
                    :columns="columns"
                    :data="data"
                    :loading="isPending"
                    :enable-search="false"
                    :enable-filters="false"
                    :default-column-visibility="defaultColumnVisibility"
                    :default-sorting="[
                        {
                            id: 'name',
                            desc: false,
                        },
                    ]"
                >
                    <template #title>
                        <CardTitle>
                            {{ t('payrolls.edit.header', { company: props.payroll.company.name }) }}
                        </CardTitle>
                    </template>
                    <template #actions>
                        <DataTableToolbarActions name="payroll">
                            <AddEmployeeToPayroll
                                v-if="hasPermission(Permission.EMPLOYEES_CREATE)"
                                :payroll="payroll"
                                :disabled="isApproved"
                            />
                            <RemoveEmployeeFromPayroll
                                v-if="hasPermission(Permission.EMPLOYEES_CREATE)"
                                :payroll="payroll"
                                :disabled="isApproved"
                                :employees="data"
                            />
                            <PayrollApprovalAlertDialog
                                v-if="
                                    hasPermission(Permission.SALARY_PAYROLLS_UPDATE) && !isApproved
                                "
                                :payroll="payroll"
                                :employees="data"
                            />
                            <PayrollUnlockAlertDialog
                                v-if="
                                    hasPermission(Permission.SALARY_PAYROLLS_UPDATE) &&
                                    isApproved &&
                                    (clientCanUnlock || !isClient)
                                "
                                :payroll="payroll"
                                :time-since-approval="timeSinceApproval"
                                :is-client="isClient"
                            />
                        </DataTableToolbarActions>
                    </template>
                </DataTable>
            </PageCard>

            <PayrollSummary
                :payroll="payroll"
                :is-approved="isApproved"
            />
        </div>

        <PayrollGageAlertDialog />
        <PayrollLaunchArrangementMonthlyAlertDialog />
        <PayrollFixedAddOnsAlertDialog />
    </AppLayout>
</template>
